import { useEffect, useRef, useState } from "react";

import { Avatar, Input, SelectInput } from "@web-app/components";
import {
  ExternalProviderKind,
  OptimizationItem,
  ProviderExtras,
} from "@web-app/types";

import { externalProviderDisplay } from "../utils";

type Props = {
  extras: ProviderExtras;
  onChange: (next: ProviderExtras) => void;
  previewItems: OptimizationItem[];
  providerKinds: ExternalProviderKind[];
};

type FieldSpec = {
  defaultValue?: string;
  key: string;
  label: string;
  options?: { label: string; value: string }[];
  placeholder?: string;
  type: "number" | "select" | "text";
};

const UBER_FIELDS: FieldSpec[] = [
  {
    defaultValue: "normal",
    key: "deliveryType",
    label: "Delivery Service",
    options: [
      { label: "Normal", value: "normal" },
      { label: "Catering", value: "catering" },
    ],
    type: "select",
  },
  {
    defaultValue: "normal",
    key: "deliveryMode",
    label: "Delivery Mode",
    options: [
      { label: "Normal", value: "normal" },
      { label: "Asap", value: "asap" },
    ],
    type: "select",
  },
  {
    defaultValue: "deliverable_action_meet_at_door",
    key: "deliverableAction",
    label: "Deliverable Action",
    options: [
      { label: "Meet at door", value: "deliverable_action_meet_at_door" },
      { label: "Leave at door", value: "deliverable_action_leave_at_door" },
    ],
    type: "select",
  },
  {
    defaultValue: "return",
    key: "undeliverableAction",
    label: "Undeliverable Action",
    options: [
      { label: "Return", value: "return" },
      { label: "Discard", value: "discard" },
      { label: "Leave at door", value: "leave_at_door" },
    ],
    type: "select",
  },
  {
    defaultValue: "medium",
    key: "itemSize",
    label: "Item Size",
    options: [
      { label: "Small eg. water bottle", value: "small" },
      { label: "Medium eg. retail bag", value: "medium" },
      { label: "Large eg. computer monitor", value: "large" },
      { label: "X-Large eg. 42 inch flat screen TV", value: "xlarge" },
    ],
    type: "select",
  },
];

const DOORDASH_FIELDS: FieldSpec[] = [
  {
    defaultValue: "normal",
    key: "deliverableAction",
    label: "Deliverable Action",
    options: [
      { label: "Normal", value: "normal" },
      { label: "Contactless Dropoff", value: "contactless_dropoff" },
    ],
    type: "select",
  },
  {
    defaultValue: "return_to_pickup",
    key: "undeliverableAction",
    label: "Undeliverable Action",
    options: [
      { label: "Return to pickup", value: "return_to_pickup" },
      { label: "Dispose", value: "dispose" },
    ],
    type: "select",
  },
  {
    defaultValue: "car",
    key: "dasherAllowedVehicles",
    label: "Allowed Vehicle Type",
    options: [
      { label: "Car", value: "car" },
      { label: "Bicycle", value: "bicycle" },
      { label: "Both", value: "both" },
    ],
    type: "select",
  },
];

const PROVIDER_FIELDS: Record<ExternalProviderKind, FieldSpec[]> = {
  DOORDASH: DOORDASH_FIELDS,
  UBER: UBER_FIELDS,
};

const fieldKeyFor = (kind: ExternalProviderKind, fieldKey: string) =>
  `${kind}:${fieldKey}`;

// Values already chosen win over the defaults, so reopening a draft shows what
// was saved rather than resetting the form.
const seedValues = (
  kinds: ExternalProviderKind[],
  extras: ProviderExtras,
  items: OptimizationItem[],
): Record<string, string> => {
  const seeded: Record<string, string> = {};
  kinds.forEach((kind) => {
    PROVIDER_FIELDS[kind].forEach((field) => {
      const saved = items
        .map((item) => extras[item.itemId]?.[kind]?.[field.key])
        .find((value) => value !== undefined && value !== "");
      const value = saved ?? field.defaultValue;
      if (value !== undefined) {
        seeded[fieldKeyFor(kind, field.key)] = String(value);
      }
    });
  });
  return seeded;
};

export default function DeliveryOptionsStep({
  extras,
  onChange,
  previewItems,
  providerKinds,
}: Props) {
  const [values, setValues] = useState<Record<string, string>>({});
  const seededFor = useRef("");

  // The defaults have to reach the payload too, otherwise the request would not
  // match the options the form is showing.
  useEffect(() => {
    if (providerKinds.length === 0 || previewItems.length === 0) return;
    const signature = [
      providerKinds.join(","),
      previewItems.map((item) => item.itemId).join(","),
    ].join("|");
    if (seededFor.current === signature) return;
    seededFor.current = signature;

    const seeded = seedValues(providerKinds, extras, previewItems);
    setValues(seeded);

    const next: ProviderExtras = { ...extras };
    let changed = false;
    previewItems.forEach((item) => {
      const perItem = { ...(next[item.itemId] ?? {}) };
      providerKinds.forEach((kind) => {
        const perProvider = { ...(perItem[kind] ?? {}) };
        PROVIDER_FIELDS[kind].forEach((field) => {
          const value = seeded[fieldKeyFor(kind, field.key)];
          if (!value || perProvider[field.key] !== undefined) return;
          perProvider[field.key] =
            field.type === "number" ? Number(value) : value;
          changed = true;
        });
        perItem[kind] = perProvider;
      });
      next[item.itemId] = perItem;
    });
    if (changed) onChange(next);
  }, [providerKinds, previewItems, extras, onChange]);

  if (providerKinds.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-500">
        No external providers selected, so there is nothing to fill.
      </div>
    );
  }

  const applyValue = (
    kind: ExternalProviderKind,
    fieldKey: string,
    value: string,
  ) => {
    setValues((v) => ({ ...v, [fieldKeyFor(kind, fieldKey)]: value }));
    const fieldSpec = PROVIDER_FIELDS[kind].find((f) => f.key === fieldKey);
    const nextExtras: ProviderExtras = { ...extras };
    previewItems.forEach((item) => {
      const perItem = { ...(nextExtras[item.itemId] ?? {}) };
      const perProvider = { ...(perItem[kind] ?? {}) };
      if (value === "") {
        delete perProvider[fieldKey];
      } else {
        perProvider[fieldKey] =
          fieldSpec?.type === "number" ? Number(value) : value;
      }
      perItem[kind] = perProvider;
      nextExtras[item.itemId] = perItem;
    });
    onChange(nextExtras);
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        These options apply to every order the provider quotes.
      </p>

      {providerKinds.map((kind) => {
        const provider = externalProviderDisplay(kind);
        return (
          <div
            key={kind}
            className="rounded-lg border border-gray-200 bg-white p-6"
          >
            <div className="flex items-center gap-2">
              <Avatar
                name={provider.name}
                image={provider.avatarUrl}
                size={6}
              />
              <p className="text-sm font-medium text-gray-900">
                {provider.name}
              </p>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {PROVIDER_FIELDS[kind].map((field) => {
                const key = fieldKeyFor(kind, field.key);
                return (
                  <div key={key} className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {field.label}
                    </label>
                    {field.type === "select" ? (
                      <SelectInput
                        options={[
                          { label: "Select…", value: "" },
                          ...(field.options ?? []),
                        ]}
                        selected={values[key] ?? ""}
                        onChange={(value) => applyValue(kind, field.key, value)}
                      />
                    ) : (
                      <Input
                        type={field.type === "number" ? "number" : "text"}
                        value={values[key] ?? ""}
                        onChange={(value) => applyValue(kind, field.key, value)}
                        placeholder={field.placeholder ?? ""}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
